import { Tab } from '@/types/AllTabs';

export type File = {
    id: number;
    name: string;
    type: string;
    fileType: string;
    uploadedAt: string;
    ownerFirstName: string;
    ownerLastName: string;
    ownerEmail: string;
    projectId: number;
    projectName: string;
};

export type Folder = {
    name: string;
    children: FileOrFolder[]; 
    projectId: number;
    favorite: boolean;
    companyName: string;
    type: string;
};

export type FileOrFolder = File | Folder; 

export type Project = {
    id: number;
    name: string;
}

export type Tabs = {
    activeIndex: number;
    tabs: Tab[];
}

export type SpecAnnotation = {
    pageNum: number;
    bboxes: number[][] | undefined;
}

export type SpecSection = {
    specId: number;
    specKey: string;
    specTitle: string;
    selected: boolean;
}

export type ListItem = {
    index: string;
    text: string | undefined;
    children: ListItem[] | undefined;
}

export type PartItem = {
    index: string;
    text: string;
    children: ListItem[];
}

export type Part = {
    partItems: PartItem[];
}

export type Section = {
    section: string;
    name: string;
    part1: Part | undefined;
    part2: Part;
    part3: Part | undefined;
}

export type SpecDictionary = {
    specId: number;
    sections: Section[]
}

export type SubmittalSpecSection = {
    submittalId: number;
    specId: number;
    specKey: string;
}

export type SubmittalAssignment = {
    id: number;
    submittalId: number;
    assignedTo: User;
    hours: number | undefined;
    role: 'MAIN' | 'PEER' | 'FINAL';
    remark: string;
    paid: boolean;
}

export type Submittal = {
    id: number;
    project: Project;
    fileName: string;
    specSections: SubmittalSpecSection[];
    processing: boolean;
    owner: User;
    comment: string;
    uploadedAt: string;
    releasedToContractorAt: string | null;
    dueFromContractorAt: string | null;
    returnedFromContractorAt: string | null;
    releasedToCustomerAt: string | null;
    dueToCustomerAt: string;
    assignments: SubmittalAssignment[];
    numPages: number;
    excellentContractorReview: boolean;
    feedback?: string;
    originalHours?: number;
    spectHours?: number;
}

export type Design = {
    id: number;
    projectId: number;
    fileName: string;
    state: "CREATED" | "PROCESSING" | "PROCESSED" | "FAILED";
    ownerId: number;
    uploadedAt: string;
    type: "spec" | "drawing";
}

export type Bbox = {
    page: number;
    bbox: number[];
}

export type Location = {
    page: number;
    bbox?: number[];
}

export type DesignBbox = Bbox & {
    design: Design;
}

export type MutableDesignBbox = Omit<DesignBbox, 'design'> & { design: Pick<Design, 'id'> & Partial<Omit<Design, 'id'>>; };
export type MutableComment = Partial<Omit<Comment, 'designBboxes'>> & {
    submittalId: Comment['submittalId'];
    designBboxes?: MutableDesignBbox[];
};

export type CommentDesignBboxes = {
    comment: Comment;
    bboxes: DesignBbox[];
}

export type Datum = {
    key: string;
    value: string;
    location: Bbox;
}

export type ProductSpecPortions = {
    specId: number;
    portions: number[][];
}

export type Product = {
    id: number;
    name: string;
    locations: Location[];
    manufacturer: string;
    specPortions: ProductSpecPortions[] | null;
    numErrors: number | null;
    numWarnings: number | null;
    numCorrect: number | null;
    numNotFound: number | null;
    reviewState: string | null;
    reviewNote: string | null;
    createdAt: string;
    selectedScheduleChecks: ScheduleCheck[];
    selectedCustomChecks: Check[];
    submittalId: number;
    instantReview: "NOT_FOUND" | "APPROVED" | "REJECTED";
    acceptableManufacturers: string[] | undefined;
    processingSince: string | undefined;
    parsedContent: Datum[] | undefined;
}

export type Check = {
    id: number;
    type: string;
    content: string;
}

export enum CommentType {
    CORRECT = "CORRECT",
    WARNING = "WARNING",
    INCORRECT = "INCORRECT",
    NOT_FOUND = "NOT_FOUND"
}

export type Comment = {
    id: number;
    createdAt: string;
    citation: string | undefined;
    requirement: string | undefined;
    content: string;
    number: number | undefined;
    pageNum: number;
    bboxes: number[][] | undefined;
    type: CommentType;
    check: Check | undefined;
    product: Product | undefined;
    submittalId: number;
    includedInCoverSheet: boolean;
    reviewed: boolean;
    rating: "GOOD" | "BAD" | "UNRATED";
    feedback: string | undefined;
    designBboxes: DesignBbox[];
    createdBy: User | undefined;
}

export type SubmittalMessage = {
    submittal: Submittal;
    products: Product[];
    comments: Comment[];
}

export type Company = {
    id: number,
    name: string,
    createdAt: string
    offerings: Offering[];
}

export type Role = "customer" | "contractor" | "admin";

export type Offering = "REVIEW" | "LATTICE";

export type User = {
    id: number;
    role: Role;
    firstName: string;
    lastName: string;
    email: string;
    company: Company | undefined;
    christian: boolean;
}

export type CustomCheckorComment = {
    id: number;
    companyId: number;
    sectionId?: number;
    checkContent?: string;
    comment?: string;
    section?: CustomCheckorCommentSpecSection;
}

export type CustomCheckorCommentSpecDivision = {
    id: number;
    number: string;
    name: string;
}

export type CustomCheckorCommentSpecSection = {
    id: number;
    division: CustomCheckorCommentSpecDivision;
    level2: string;
    level3: string;
    level4: string | null;
    title: string;
}

export type Schedule = {
    id: number;
    drawingId: number;
    page: number;
    name: string;
    bbox: number[];
}

export type Tag = {
    id: number;
    schedule: Schedule;
    name: string;
}

export type ScheduleCheck = Check & {
    schedule: Schedule;
    bboxes: number[][];
    tag?: Tag;
}

export type UsageData = {
    month: string;
    allotment: number;
    usages: {
        submittalId: number;
        submittalFileName: string;
        pageCount: number;
        projectName: string;
        projectId: number;
        submittalReleasedToCustomerAt: string;
        submittalUploadedAt: string;
        ownerId: number;
        ownerFirstName: string;
        ownerLastName: string;
        submittalPages: number;
        commentCount: number;
    }[];
}

export type Contractor = {
    id: number;
    role: Role;
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    enabled: boolean;
    lead: boolean;
    christian: boolean;
    wiseId: string;
    rate: number;
}

export type InvoiceItem = {
    id: number;
    createdAt: string;
    contractor: Contractor;
    hours: number;
    remark: string;
    paid: boolean;
    pay: number;
}
